import { Component, inject, input } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { TimelineModule } from 'primeng/timeline';
import { CardModule } from 'primeng/card';
import { switchMap, map, startWith, of, delay, catchError } from 'rxjs';
import { ReservationsHistoryService } from './reservation-history.service';
import { LoaderComponent } from '@app/shared/components/loader/loader.component';
import { AsyncPipe } from '@angular/common';
import { DateAgoPipe } from '@app/pipes/date-ago.pipe';
import { ReservationStatusesDisplayNames, ReservationVersion } from '../reservations.model';
import { ReservationHistoryListItem } from './reservation-history.model';
import { Router } from '@angular/router';

// Keep the timeline mounted for the accordion's collapse animation before clearing it.
const PANEL_COLLAPSE_ANIMATION_MS = 300;
const LOADING_STATE = { isLoading: true, isVisible: true, history: null, error: null };
const HIDDEN_STATE = { isLoading: false, isVisible: false, history: null, error: null };

@Component({
  selector: 'app-reservation-history',
  standalone: true,
  imports: [TimelineModule, LoaderComponent, AsyncPipe, CardModule, DateAgoPipe],
  templateUrl: './reservation-history.component.html',
  styleUrls: ['./reservation-history.component.scss']
})
export class ReservationHistoryComponent {
  private _reservationHistoryService = inject(ReservationsHistoryService);
  private _router = inject(Router);

  reservationUid = input.required<string>();
  isVisible = input.required<boolean>();
  currentVersion = input<ReservationVersion>('latest');

  private _loadHistory$ = toObservable(this.reservationUid).pipe(
    switchMap((uid) => this._reservationHistoryService.getReservationHistory(uid)),
    map((history) => ({ isLoading: false, isVisible: true, history: this.toTimeline(history), error: null })),
    catchError((error: HttpErrorResponse) => of(this.toErrorState(error))),
    startWith(LOADING_STATE)
  );

  vm$ = toObservable(this.isVisible).pipe(
    switchMap((isVisible) =>
      isVisible ? this._loadHistory$ : of(HIDDEN_STATE).pipe(delay(PANEL_COLLAPSE_ANIMATION_MS))
    )
  );

  navigateToReservationWithVersion(version: number, isLatest = false) {
    this._router.navigate(['/operations/reservations'], {
      queryParams: { selected: this.reservationUid(), selectedVersion: isLatest ? null : version },
      queryParamsHandling: 'merge'
    });
  }

  // Newest version first; each entry knows whether its status changed and whether it is the current/latest version.
  private toTimeline(history: ReservationHistoryListItem[]) {
    const newestFirst = [...history].reverse();
    return newestFirst.map((entry, index) => this.toTimelineEvent(entry, index, newestFirst));
  }

  private toTimelineEvent(entry: ReservationHistoryListItem, index: number, newestFirst: ReservationHistoryListItem[]) {
    const olderEntry = newestFirst[index + 1];
    return {
      ...entry,
      changedDate: this.toUtcDate(entry.changedDate),
      statusDisplayName: ReservationStatusesDisplayNames[Number(entry.reservationStatus)],
      showStatus: entry.reservationStatus !== olderEntry?.reservationStatus,
      isLatest: index === 0,
      isCurrentVersion: this.isCurrentVersion(entry.version, index)
    };
  }

  private isCurrentVersion(version: number, index: number): boolean {
    const current = this.currentVersion();
    return current === version || (index === 0 && (!current || current === 'latest'));
  }

  private toErrorState(error: HttpErrorResponse) {
    const message =
      error.status === 404
        ? 'No change history is available for this reservation.'
        : 'Failed to load the change history. Please try again.';
    return { isLoading: false, isVisible: true, history: null, error: message };
  }

  private toUtcDate(value: string): Date {
    return new Date(new Date(value) + 'Z');
  }
}
